// sticky-add-to-cart.js
// Sticky add-to-cart bar — shows when the main ATC button scrolls out of view.
// Listens for variant:change events to keep price and availability in sync.

import { formatMoney, getThemeSettings } from '@stardust/utilities';

let observer = null;     // IntersectionObserver watching the main ATC button
let bodyObserver = null; // MutationObserver waiting for the late Gorgias chat button

function init() {
  // Clean up observers from a previous init. init() runs again on every
  // theme:section:morph, so anything created here must be torn down first or
  // it accumulates one observer per variant switch.
  observer?.disconnect();
  observer = null;
  bodyObserver?.disconnect();
  bodyObserver = null;

  const bar = document.querySelector('[data-sticky-add-to-cart]');
  const mainBtn = document.querySelector('[data-add-to-cart]');
  if (!bar || !mainBtn) return;

  // Note: the add-to-cart click is bound once via document delegation (see below),
  // not here — init() re-runs on every section morph, and the button node is
  // reused in place, so binding here would stack duplicate listeners.

  // Show/hide based on main button visibility
  // Chat button (Gorgias) is an iframe that loads late — query lazily and
  // use a MutationObserver to catch it appearing while sticky bar is already visible
  let chatBtnOriginalBottom = null;
  let chatBtnCached = null;
  let stickyVisible = false;

  function getChatButton() {
    if (!chatBtnCached) {
      chatBtnCached = document.getElementById('chat-button');
      if (chatBtnCached) {
        chatBtnOriginalBottom = chatBtnCached.style.bottom;
      }
    }
    return chatBtnCached;
  }

  function syncChatButton() {
    const chatBtn = getChatButton();
    if (chatBtn) {
      chatBtn.style.bottom = stickyVisible ? '80px' : chatBtnOriginalBottom;
    }
  }

  // Watch for late-injected #chat-button (Gorgias iframe)
  if (!getChatButton()) {
    bodyObserver = new MutationObserver(() => {
      if (getChatButton()) {
        bodyObserver?.disconnect();
        bodyObserver = null;
        syncChatButton();
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  observer = new IntersectionObserver(([entry]) => {
    stickyVisible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    bar.dataset.visible = stickyVisible;
    syncChatButton();
  });

  observer.observe(mainBtn);
}

// Forward the sticky button click to the main buy-box button, which owns the real
// form, variant input, and data-button-type (add-to-cart / pre-order / out-of-stock)
// logic. This is what makes every state behave like the buy box: add-to-cart and
// pre-order add to the cart, and out-of-stock opens the Klaviyo back-in-stock form
// (the main button carries the klaviyo-bis-trigger class and sits inside the product
// form, so Klaviyo resolves the selected variant). Delegated on document so it
// survives section morphs and binds exactly once.
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-sticky-add-to-cart-btn]');
  if (!btn || btn.disabled) return;
  document.querySelector('[data-add-to-cart]')?.click();
});

// Resolves the server-computed button type for a variant from the same
// [data-variant-button-states] map product.js#resolveButtonType reads, so a
// stale/invalid pre-order date (now out-of-stock) or a pre-order variant is
// reflected immediately instead of guessing from variant.available alone.
function resolveButtonType(variantId) {
  const mapEl = document.querySelector('[data-variant-button-states]');
  if (!mapEl) return null;

  try {
    const states = JSON.parse(mapEl.textContent || '{}');
    return states[String(variantId)] ?? null;
  } catch {
    return null;
  }
}

// Variant changes — use live DOM queries so it works after morphs
document.addEventListener('variant:change', (e) => {
  const variant = e.detail?.variant;
  if (!variant) return;

  const bar = document.querySelector('[data-sticky-add-to-cart]');
  if (!bar) return;

  const priceContainer = bar.querySelector('[data-buy-box-price]');
  if (priceContainer && variant.price != null) {
    const isOosMode =
      getThemeSettings().hideSalePriceWhenOos === true &&
      priceContainer.dataset.productAvailable === 'false';

    if (isOosMode) {
      // Liquid renders [data-compare-price] when compare_at > 0, [data-price] as fallback.
      const displayPrice = variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
      const priceEl =
        priceContainer.querySelector('[data-compare-price]') ??
        priceContainer.querySelector('[data-price]');
      if (priceEl) {
        priceEl.textContent = formatMoney(displayPrice);
      }
    } else {
      const isOnSale = variant.compare_at_price && variant.compare_at_price > variant.price;

      // Update data attributes (drives Tailwind group- CSS)
      priceContainer.dataset.onSale = isOnSale;
      priceContainer.dataset.productAvailable = variant.available;
      priceContainer.dataset.priceValue = variant.price;
      priceContainer.dataset.comparePriceValue = variant.compare_at_price || 0;

      // Update displayed text
      const priceEl = priceContainer.querySelector('[data-price]');
      const compareEl = priceContainer.querySelector('[data-compare-price]');
      const saleTag = priceContainer.querySelector('[data-sale-tag]');

      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (compareEl) compareEl.textContent = formatMoney(variant.compare_at_price);
      if (saleTag && isOnSale && variant.compare_at_price > 0) {
        const pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
        saleTag.textContent = `${pct}% OFF`;
      }
    }
  }

  // Mirror the buy-box button morph (product.js #applyVariantPreview): resolve the
  // same server-computed button type so pre-order and stale-date pre-order (now
  // out-of-stock) are reflected immediately, not just plain variant.available. The
  // button is never disabled — when out of stock it stays clickable so its
  // forwarded click can open the Klaviyo back-in-stock form, matching the buy box.
  const btn = bar.querySelector('[data-sticky-add-to-cart-btn]');
  if (btn) {
    btn.disabled = false;
    const buttonType = resolveButtonType(variant.id) ?? (variant.available ? 'add-to-cart' : 'out-of-stock');
    btn.dataset.buttonType = buttonType;

    if (buttonType === 'out-of-stock') {
      btn.textContent = btn.dataset.oosButtonText || 'Sold Out - Get Notified';
    } else if (buttonType === 'pre-order') {
      btn.textContent = btn.dataset.preorderButtonText || 'Pre-Order';
    } else {
      btn.textContent = btn.dataset.addToCartText || 'Add to Cart';
    }
  }
});

// Re-init after section morphs (observer + qty controls need fresh DOM refs)
document.addEventListener('theme:section:morph', () => init());

init();
